import React, { useState, useCallback, useMemo } from 'react';
import {
  useStripe,
  useElements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from '@stripe/react-stripe-js';
import { PayPalButtons } from '@paypal/react-paypal-js';
import axios from 'axios';

function CheckoutFormInner({
  event,
  paymentMethod = 'card',
  paymentRequest,
  onSuccess,
  effectivePrice,
  promoCode = '',
}) {
  const stripe = useStripe();
  const elements = useElements();

  const [cardHolderName, setCardHolderName] = useState('');
  const [numberOK, setNumberOK] = useState(false);
  const [expOK, setExpOK] = useState(false);
  const [cvcOK, setCvcOK] = useState(false);
  const [errorText, setErrorText] = useState(null);
  const [shakeFlag, setShakeFlag] = useState(false);
  const [loading, setLoading] = useState(false);

  const ready = useMemo(
    () => cardHolderName.trim().length && numberOK && expOK && cvcOK,
    [cardHolderName, numberOK, expOK, cvcOK]
  );

  const resetCardForm = () => {
    elements?.getElement(CardNumberElement)?.clear();
    elements?.getElement(CardExpiryElement)?.clear();
    elements?.getElement(CardCvcElement)?.clear();

    setCardHolderName('');
    setNumberOK(false);
    setExpOK(false);
    setCvcOK(false);
  };

  const triggerFailure = (msg) => {
    setErrorText(msg || 'Something went wrong.');
    setShakeFlag(true);
    resetCardForm();
    setTimeout(() => setShakeFlag(false), 500);
  };

  const clearFailure = () => {
    if (errorText) setErrorText(null);
  };

  const submit = useCallback(
    async (e) => {
      e.preventDefault();

      if (!ready || loading || !stripe || !elements) return;

      setLoading(true);
      setErrorText(null);

      try {
        const cardElement = elements.getElement(CardNumberElement);

        if (!cardElement) {
          triggerFailure('Card form is not ready.');
          setLoading(false);
          return;
        }

        const { error: pmErr, paymentMethod: pm } =
          await stripe.createPaymentMethod({
            type: 'card',
            card: cardElement,
            billing_details: {
              name: cardHolderName.trim(),
            },
          });

        if (pmErr) {
          triggerFailure(pmErr.message);
          setLoading(false);
          return;
        }

        const token = localStorage.getItem('accessToken') || '';

        const { data } = await axios.post(
          '/api/payments/save-payment-method',
          {
            paymentMethodId: pm.id,
            cardBrand: pm.card.brand,
            last4: pm.card.last4,
            expiryMonth: pm.card.exp_month,
            expiryYear: pm.card.exp_year.toString().slice(-2),
            cardHolderName: cardHolderName.trim(),
          },
          {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            withCredentials: true,
          }
        );

        if (data?.success) {
          onSuccess?.({
            paymentMethodId: pm.id,
            cardBrand: pm.card.brand,
            last4: pm.card.last4,
            expiryMonth: pm.card.exp_month,
            expiryYear: pm.card.exp_year,
            cardHolderName: cardHolderName.trim(),
            savedMethod: data?.paymentMethod || data?.savedMethod || data,
          });

          resetCardForm();
        } else {
          triggerFailure(data?.message || 'Failed to save card.');
        }
      } catch (err) {
        triggerFailure(
          err?.response?.data?.message ||
            err?.message ||
            'Error saving card.'
        );
      } finally {
        setLoading(false);
      }
    },
    [ready, loading, stripe, elements, cardHolderName, onSuccess]
  );

  if (paymentMethod === 'paypal') {
    return (
      <div style={st.paypalContainer}>
        <PayPalButtons
          style={{ layout: 'vertical', height: 40, tagline: false }}
          createOrder={async () => {
            const token = localStorage.getItem('accessToken') || '';

            const { data } = await axios.post(
              '/api/payments/create-paypal-order',
              {
                eventId: event._id,
                promoCode,
              },
              {
                headers: token ? { Authorization: `Bearer ${token}` } : {},
                withCredentials: true,
              }
            );

            return data.orderID;
          }}
          onApprove={async ({ orderID }) => {
            const token = localStorage.getItem('accessToken') || '';

            const { data } = await axios.post(
              '/api/payments/capture-paypal-order',
              {
                orderID,
                eventId: event._id,
              },
              {
                headers: token ? { Authorization: `Bearer ${token}` } : {},
                withCredentials: true,
              }
            );

            onSuccess?.({
              prebuiltTicket: data?.userTicket,
              paymentMethodOverride: 'paypal',
            });
          }}
        />
      </div>
    );
  }

  if (
    (paymentMethod === 'apple_pay' || paymentMethod === 'google_pay') &&
    paymentRequest
  ) {
    return null;
  }

  const fieldCls = errorText ? 'error-field' : '';
  const shakeCls = shakeFlag ? 'shake' : '';

  return (
    <form onSubmit={submit} noValidate>
      <div style={st.container}>
        <label style={st.label}>Cardholder Name</label>
        <input
          className={`${fieldCls} ${shakeCls}`}
          style={st.field}
          value={cardHolderName}
          placeholder="Full Name"
          onChange={(e) => {
            setCardHolderName(e.target.value);
            clearFailure();
          }}
        />

        <label style={st.label}>Card Number</label>
        <div className={`${fieldCls} ${shakeCls}`} style={st.field}>
          <CardNumberElement
            options={{ ...cardStyle, showIcon: true }}
            onChange={(e) => {
              setNumberOK(e.complete);
              clearFailure();
            }}
          />
        </div>

        <div style={st.row}>
          <div style={{ flex: 1 }}>
            <label style={st.label}>Expiry</label>
            <div className={`${fieldCls} ${shakeCls}`} style={st.field}>
              <CardExpiryElement
                options={cardStyle}
                onChange={(e) => {
                  setExpOK(e.complete);
                  clearFailure();
                }}
              />
            </div>
          </div>

          <div style={{ flex: 1 }}>
            <label style={st.label}>CVC</label>
            <div className={`${fieldCls} ${shakeCls}`} style={st.field}>
              <CardCvcElement
                options={cardStyle}
                onChange={(e) => {
                  setCVCOKSafe(e.complete);
                  clearFailure();
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <div
        className={`error-banner ${errorText ? 'show' : ''}`}
        style={st.errorBanner}
      >
        {errorText}
      </div>

      <div style={st.footer}>
        <button
          type="submit"
          disabled={!stripe || !elements || loading || !ready}
          style={{
            ...st.button,
            opacity: !stripe || !elements || loading || !ready ? 0.5 : 1,
            cursor:
              !stripe || !elements || loading || !ready
                ? 'not-allowed'
                : 'pointer',
          }}
        >
          {loading ? <div style={st.spinner} /> : 'Save Card'}
        </button>
      </div>

      <style>{`
        .error-field { border: 2px solid #E63946 !important; }
        .shake { animation: shake 0.4s both; }
        .error-banner { opacity: 0; max-height: 0; transition: opacity .25s, max-height .25s; }
        .error-banner.show { opacity: 1; max-height: 60px; }
        input:focus,
        input:-webkit-autofill,
        input:-webkit-autofill:focus {
          outline: none !important;
          box-shadow: none !important;
          background-color: #fff !important;
        }
        @keyframes shake {
          10%, 90% { transform: translateX(-1px); }
          20%, 80% { transform: translateX(2px); }
          30%, 50%, 70% { transform: translateX(-4px); }
          40%, 60% { transform: translateX(4px); }
        }
        @keyframes spin { to { transform: rotate(360deg); } }
        input::placeholder { color: #aaa; }
      `}</style>
    </form>
  );

  function setCVCOKSafe(value) {
    setCvcOK(value);
  }
}

const cardStyle = {
  style: {
    base: {
      fontSize: '14px',
      fontFamily:
        "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
      color: '#0B0B0B',
      iconColor: '#555',
      '::placeholder': { color: '#aaa', fontWeight: 500 },
    },
    invalid: {
      color: '#E63946',
      iconColor: '#E63946',
    },
  },
};

const st = {
  container: {
    margin: '0 0 0px',
  },

  paypalContainer: {
    margin: 20,
  },

  label: {
    fontSize: 12,
    fontWeight: 800,
    color: '#6B7280',
    marginBottom: 6,
    textTransform: 'uppercase',
    fontFamily:
      "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
  },

  field: {
    width: '100%',
    padding: 12,
    borderRadius: 10,
    marginTop: 4,
    marginBottom: 14,
    fontSize: 12,
    color: '#0B0B0B',
    fontWeight: 600,
    fontFamily:
      "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
    boxSizing: 'border-box',
    border: '1px solid #EEF0F2',
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
    transition: 'border-color .25s',
  },

  row: {
    display: 'flex',
    gap: 8,
  },

  errorBanner: {
    background: '#FEECEC',
    color: '#E63946',
    padding: '10px 14px',
    borderRadius: 10,
    margin: '0 20px 16px',
    textAlign: 'center',
    fontSize: 14,
    fontFamily:
      "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
  },

  footer: {
    position: 'fixed',
    bottom: 0,
    left: '50%',
    transform: 'translateX(-50%)',
    width: '100%',
    maxWidth: 480,
    backgroundColor: '#fff',
    padding: '16px 20px 20px',
    borderTop: '1px solid #EEF0F2',
    zIndex: 9999,
    boxSizing: 'border-box',
  },

  button: {
    width: '100%',
    height: 44,
    padding: '12px 0',
    backgroundColor: '#0B0B0B',
    color: '#fff',
    border: '1px solid #0B0B0B',
    borderRadius: 10,
    fontSize: 14,
    fontWeight: 800,
    cursor: 'pointer',
    transition: 'opacity .2s',
    fontFamily:
      "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  spinner: {
    width: 16,
    height: 16,
    margin: '0 auto',
    border: '2px solid #fff',
    borderTop: '2px solid rgba(255,255,255,0.3)',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
};

export default React.memo(CheckoutFormInner);